/**
 * i18n Inspector Registry
 *
 * In-memory store that tracks which i18n keys are rendered on the current screen.
 * Populated automatically when handleValidateCodevalue() translates a key.
 */

const valueToKeys = new Map(); // displayValue -> [key1, key2, ...]
const keyToValue = new Map();  // key -> displayValue (for panel list)
const listeners = new Set();   // callbacks notified when registry changes

export const i18nRegistry = {
    /**
     * Register a key -> display value pair.
     * Called automatically by handleValidateCodevalue().
     */
    register(displayValue, key) {
        if (!displayValue || !key) return;

        // value -> keys lookup
        const existingKeys = valueToKeys.get(displayValue) ?? [];
        if (!existingKeys.includes(key)) {
            valueToKeys.set(displayValue, [...existingKeys, key]);
        }

        // key -> value
        if (keyToValue.get(key) !== displayValue) {
            keyToValue.set(key, displayValue);
            listeners.forEach((fn) => fn());
        }
    },

    /**
     * Return all key-value pairs registered on the current screen.
     */
    getAll() {
        const entries = [];
        keyToValue.forEach((value, key) => {
            entries.push({ key, value });
        });
        return entries.sort((a, b) => a.key.localeCompare(b.key));
    },

    /**
     * Update value for a key in memory (after editing in drawer).
     */
    update(key, newValue) {
        if (!key) return;
        keyToValue.set(key, newValue);
        listeners.forEach((fn) => fn());
    },

    /**
     * Subscribe to registry changes.
     */
    subscribe(fn) {
        listeners.add(fn);
        return () => listeners.delete(fn);
    },

    /**
     * Reset the registry (called when navigating to a new page).
     */
    clear() {
        valueToKeys.clear();
        keyToValue.clear();
        listeners.forEach((fn) => fn());
    },
};
